import { useState } from "react";

import { fetchEnrollmentEodCsv } from "./dashboardsReactQuery";
import { analysisDateLabel, formatEtDateTime } from "./enrollmentShared";
import type {
  EnrollmentConversionDetail,
  EnrollmentCumulativeRow,
  EnrollmentEodCalls,
  EnrollmentSnapshotMetrics,
} from "./enrollmentTypes";
import {
  CallsSummarySection,
  CumulativeEnrollmentChart,
  DailyConversionDetailsTable,
  EodSnapshotPanel,
  ErrorState,
  LoadingState,
  ScheduledCallsChart,
  SectionDivider,
  SnapshotHistoryTable,
} from "./enrollmentUi";

export function EnrollmentEodTab(props: {
  comparisonLabel: string;
  comparisonSnap: EnrollmentSnapshotMetrics | null;
  calls: EnrollmentEodCalls | undefined;
  callsError: string;
  callsLoading: boolean;
  conversionRows: EnrollmentConversionDetail[];
  conversionError: string;
  conversionLoading: boolean;
  cumulativeRows: EnrollmentCumulativeRow[];
  cumulativeError: string;
  cumulativeLoading: boolean;
  snapshotHistoryError: string;
  snapshotHistoryLoading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  recentDates: string[];
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  snapError: string;
  snapLoading: boolean;
  snapshot: EnrollmentSnapshotMetrics | null;
  snapshotHistoryRows: Array<{ date: string; data: EnrollmentSnapshotMetrics | null }>;
}) {
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState("");

  const cumulativeRows = props.cumulativeRows.map((row) => ({
    date: row.date,
    alignedCount: row.alignedCount,
    pendingWithCmsCount: row.pendingWithCmsCount,
    pendingReferralCount: row.pendingReferralCount,
    pendingSecondCallCount: row.pendingSecondCallCount,
    total:
      row.alignedCount +
      row.pendingWithCmsCount +
      row.pendingReferralCount +
      row.pendingSecondCallCount,
  }));

  const matchedCumulativeRows = props.cumulativeRows.map((row) => ({
    date: row.date,
    alignedCount: row.matchedAlignedCount,
    pendingWithCmsCount: row.matchedPendingWithCmsCount,
    pendingReferralCount: row.matchedPendingReferralCount,
    pendingSecondCallCount: row.matchedPendingSecondCallCount,
    total:
      row.matchedAlignedCount +
      row.matchedPendingWithCmsCount +
      row.matchedPendingReferralCount +
      row.matchedPendingSecondCallCount,
  }));

  const handleDownloadCsv = async () => {
    try {
      setCsvLoading(true);
      setCsvError("");
      const result = await fetchEnrollmentEodCsv(props.selectedDate);
      const csv = [
        "date counted,CS UUID,Category",
        ...result.rows.map((row) => `${row.dateCounted},${row.carespaceId},${row.category}`),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `enrollment-eod-${props.selectedDate}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setCsvError(error instanceof Error ? error.message : "CSV is unavailable. Please try again.");
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-foreground">Analysis date:</span>
        <div className="flex flex-wrap gap-1.5">
          {props.recentDates.map((date) => (
            <button
              key={date}
              type="button"
              onClick={() => props.setSelectedDate(date)}
              className={
                props.selectedDate === date
                  ? "rounded-md border-2 border-foreground bg-muted px-2.5 py-1 text-xs font-semibold"
                  : "rounded-md border border-border bg-background px-2.5 py-1 text-xs text-muted-foreground"
              }
            >
              {analysisDateLabel(date)}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={props.onRefresh}
          disabled={props.refreshing}
          className="rounded-md border border-border bg-background px-3 py-1.5 text-xs"
        >
          {props.refreshing ? "Refreshing…" : "Refresh data"}
        </button>
      </div>

      <SectionDivider label="EOD snapshot" />
      {props.snapLoading ? (
        <LoadingState />
      ) : props.snapError ? (
        <ErrorState message={props.snapError} />
      ) : (
        <EodSnapshotPanel
          snap={props.snapshot}
          comparisonSnap={props.comparisonSnap}
          date={props.selectedDate}
          comparisonLabel={props.comparisonLabel}
        />
      )}

      {props.snapshotHistoryError ? <ErrorState message={props.snapshotHistoryError} /> : null}
      {props.snapshotHistoryLoading ? <LoadingState /> : null}
      <SnapshotHistoryTable rows={props.snapshotHistoryRows} />

      <SectionDivider label="Daily conversion" />
      {props.callsLoading ? (
        <LoadingState />
      ) : props.callsError ? (
        <ErrorState message={props.callsError} />
      ) : (
        <CallsSummarySection calls={props.calls} />
      )}
      {props.conversionLoading ? (
        <LoadingState />
      ) : props.conversionError ? (
        <ErrorState message={props.conversionError} />
      ) : (
        <DailyConversionDetailsTable rows={props.conversionRows} />
      )}

      <SectionDivider label="Enrollments - matched to Enrollment calls this month" />
      {props.cumulativeLoading ? (
        <LoadingState />
      ) : props.cumulativeError ? (
        <ErrorState message={props.cumulativeError} />
      ) : (
        <CumulativeEnrollmentChart
          title="Enrollments - matched to Enrollment calls this month"
          subtitle="Only carespaces with an Enrollment / Referral cal.com booking in the current month."
          rows={matchedCumulativeRows}
          action={
            <button
              type="button"
              onClick={() => void handleDownloadCsv()}
              disabled={csvLoading}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-xs"
            >
              {csvLoading ? "Preparing CSV..." : "Download CSV"}
            </button>
          }
        />
      )}

      {csvError ? <ErrorState message={csvError} /> : null}
      <SectionDivider label="Scheduled enrollment calls" />
      {props.callsLoading ? (
        <LoadingState />
      ) : props.callsError ? (
        <ErrorState message={props.callsError} />
      ) : (
        <ScheduledCallsChart rows={props.calls?.scheduledRows || []} />
      )}

      <SectionDivider label="Cumulative enrollment" />
      {props.cumulativeLoading ? (
        <LoadingState />
      ) : props.cumulativeError ? (
        <ErrorState message={props.cumulativeError} />
      ) : (
        <CumulativeEnrollmentChart
          title="Cumulative enrollment - does not account for day of enrollment call"
          subtitle={`Current month through ${formatEtDateTime(props.selectedDate)}. Weekend and U.S. federal holiday columns are shaded.`}
          rows={cumulativeRows}
        />
      )}
    </div>
  );
}
